/**
 * FORMATTING UTILITIES
 * Centralized formatting functions for consistent display across the app.
 */

// =============================================================================
// CURRENCY FORMATTING
// =============================================================================

/**
 * Format amount as Kenyan Shillings
 * @example formatCurrency(1500000) => "KES 1,500,000"
 */
export const formatCurrency = (
  amount: number,
  currency: string = 'KES'
): string => {
  return new Intl.NumberFormat('en-KE', {
    style: 'currency',
    currency: currency,
    maximumFractionDigits: 0,
  }).format(amount);
};

/**
 * Format price with shorthand (K, M, B)
 * @example formatPriceShort(1500000) => "1.5M"
 */
export const formatPriceShort = (amount: number): string => {
  if (amount >= 1_000_000_000) {
    return `${(amount / 1_000_000_000).toFixed(1).replace(/\.0$/, '')}B`;
  }
  if (amount >= 1_000_000) {
    return `${(amount / 1_000_000).toFixed(1).replace(/\.0$/, '')}M`;
  }
  if (amount >= 1_000) {
    return `${(amount / 1_000).toFixed(0)}K`;
  }
  return amount.toString();
};

/**
 * Format price range for display
 * @example formatPriceRange(5000000, 15000000) => "KES 5M - 15M"
 */
export const formatPriceRange = (min: number, max: number): string => {
  return `KES ${formatPriceShort(min)} - ${formatPriceShort(max)}`;
};

// =============================================================================
// DATE & TIME FORMATTING
// =============================================================================

/**
 * Format timestamp as relative time
 * @example formatRelativeTime(Date.now() - 3600000) => "1 hour ago"
 */
export const formatRelativeTime = (timestamp: number): string => {
  const now = Date.now();
  const diff = now - timestamp;
  
  const seconds = Math.floor(diff / 1000);
  const minutes = Math.floor(seconds / 60);
  const hours = Math.floor(minutes / 60);
  const days = Math.floor(hours / 24);
  const weeks = Math.floor(days / 7);
  const months = Math.floor(days / 30);
  
  if (months > 0) return `${months} month${months > 1 ? 's' : ''} ago`;
  if (weeks > 0) return `${weeks} week${weeks > 1 ? 's' : ''} ago`;
  if (days > 0) return `${days} day${days > 1 ? 's' : ''} ago`;
  if (hours > 0) return `${hours} hour${hours > 1 ? 's' : ''} ago`;
  if (minutes > 0) return `${minutes} minute${minutes > 1 ? 's' : ''} ago`;
  return 'Just now';
};

/**
 * Format timestamp as readable date
 * @example formatDate(1704067200000) => "Jan 1, 2024"
 */
export const formatDate = (timestamp: number): string => {
  return new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  }).format(new Date(timestamp));
};

/**
 * Format timestamp as date and time
 * @example formatDateTime(1704067200000) => "Jan 1, 2024, 12:00 PM"
 */
export const formatDateTime = (timestamp: number): string => {
  return new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  }).format(new Date(timestamp));
};

// =============================================================================
// PHONE NUMBER FORMATTING
// =============================================================================

/**
 * Format Kenyan phone number for display
 * @example formatPhoneNumber("+254712345678") => "+254 712 345 678"
 */
export const formatPhoneNumber = (phone: string): string => {
  const cleaned = phone.replace(/\D/g, '');
  
  if (cleaned.startsWith('254')) {
    const number = cleaned.slice(3);
    return `+254 ${number.slice(0, 3)} ${number.slice(3, 6)} ${number.slice(6)}`;
  }
  
  if (cleaned.startsWith('0')) {
    return `${cleaned.slice(0, 4)} ${cleaned.slice(4, 7)} ${cleaned.slice(7)}`;
  }
  
  return phone;
};

/**
 * Format phone for M-Pesa (remove + and spaces)
 * @example formatPhoneForMpesa("+254 712 345 678") => "254712345678"
 */
export const formatPhoneForMpesa = (phone: string): string => {
  const cleaned = phone.replace(/\D/g, '');
  if (cleaned.startsWith('0')) {
    return '254' + cleaned.slice(1);
  }
  return cleaned;
};

// =============================================================================
// AREA & MEASUREMENTS
// =============================================================================

/**
 * Format area in square meters
 * @example formatArea(250) => "250 m²"
 */
export const formatArea = (sqm: number): string => {
  return `${sqm.toLocaleString()} m²`;
};

/**
 * Format area in acres (for land)
 * @example formatAcres(4046.86) => "1 acre"
 */
export const formatAcres = (sqm: number): string => {
  const acres = sqm / 4046.86;
  if (acres < 1) {
    return `${(acres * 4).toFixed(1)} roods`;
  }
  return `${acres.toFixed(2)} acre${acres !== 1 ? 's' : ''}`;
};

// =============================================================================
// PRICE INPUT FORMATTING (for forms)
// =============================================================================

/**
 * Format number as you type with thousand separators
 * @example formatPriceInput("1500000") => "1,500,000"
 * @example formatPriceInput("15,00,000") => "1,500,000" (fixes misplaced commas)
 */
export const formatPriceInput = (value: string): string => {
  // Remove all non-digit characters
  const digits = value.replace(/\D/g, '');

  // If empty, return empty string
  if (!digits) return '';

  // Format with thousand separators
  return Number(digits).toLocaleString('en-US');
};

/**
 * Parse formatted price string back to number
 * @example parsePriceInput("1,500,000") => 1500000
 * @example parsePriceInput("") => 0
 */
export const parsePriceInput = (value: string): number => {
  const digits = value.replace(/\D/g, '');
  return digits ? parseInt(digits, 10) : 0;
};

/**
 * Handle price input change event
 * Returns both display value and numeric value
 */
export const handlePriceInputChange = (
  e: React.ChangeEvent<HTMLInputElement>
): { displayValue: string; numericValue: number } => {
  const displayValue = formatPriceInput(e.target.value);
  const numericValue = parsePriceInput(e.target.value);
  return { displayValue, numericValue };
};

// =============================================================================
// PROPERTY TYPE FORMATTING
// =============================================================================

/**
 * Format property type for display
 * Converts database values like 'new_development' to 'New Development'
 * Also handles legacy DB values for backwards compatibility
 * @example formatPropertyType('new_development') => "New Development"
 * @example formatPropertyType('villa') => "Villa"
 * @example formatPropertyType('house') => "Bungalow" (legacy mapping)
 */
export const formatPropertyType = (type: string): string => {
  const typeMap: Record<string, string> = {
    // Current DB values → display labels
    'apartment': 'Apartment',
    'villa': 'Villa',
    'townhouse': 'Townhouse',
    'bungalow': 'Bungalow',
    'plot': 'Plot',
    'new_development': 'New Development',
    // Legacy DB values → display labels (backwards compatibility)
    'house': 'Bungalow',
    'land': 'Plot',
    'commercial': 'New Development',
  };

  return typeMap[type?.toLowerCase()] ||
    type?.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()) || 'Property';
};

// =============================================================================
// TEXT UTILITIES
// =============================================================================

/**
 * Truncate text with ellipsis
 * @example truncateText("Long text here", 10) => "Long te..."
 */
export const truncateText = (text: string, maxLength: number): string => {
  if (text.length <= maxLength) return text;
  return text.slice(0, maxLength - 3) + '...';
};

/**
 * Pluralize word based on count
 * @example pluralize(3, "bedroom", "bedrooms") => "3 bedrooms"
 */
export const pluralize = (
  count: number,
  singular: string,
  plural?: string
): string => {
  const word = count === 1 ? singular : (plural || `${singular}s`);
  return `${count} ${word}`;
};

/**
 * Format bedroom/bathroom count
 * @example formatRooms(3, 2) => "3 bed · 2 bath"
 */
export const formatRooms = (bedrooms: number, bathrooms: number): string => {
  return `${bedrooms} bed · ${bathrooms} bath`;
};
